import { useLocation } from "react-router-dom";
import React, { useContext, useMemo, useRef, useState, useEffect } from "react";
import { Autocomplete, Box, Chip, ClickAwayListener, Popper, styled, TextField } from "@mui/material";

import { extractUserDataFromPermission } from "../../../Utils/Utils";
import { AutocompleteStationListPop } from "../../AutocompleteStationListPop/AutocompleteStationListPop";
import { useImmer } from "use-immer";
import { useDispatch, useSelector } from "react-redux";
import {
  selectedCustomersSelector,
  selectedDivisionsSelector,
  selectedZonesSelector,
  setSelectedCustomer,
  setSelectedCustomers,
  setSelectedDivision,
  setSelectedDivisions,
  setSelectedZone,
  setSelectedZones,
  singleSelectedCustomerSelector,
  singleSelectedDivisionSelector,
  singleSelectedZoneSelector,
} from "../../../features/authentication/auth-slice";
import toast from "react-hot-toast";
import { ActiveAlarmsData } from "../../Alarms/ActiveAlarms";

const StyledPopper = styled(Popper)(({ theme }) => ({
  padding: 12,
  border: `1px solid ${theme.palette.mode === "light" ? "#e1e4e8" : "#30363d"}`,
  boxShadow: `0 8px 24px ${theme.palette.mode === "light" ? "rgba(149, 157, 165, 0.2)" : "rgb(1, 4, 9)"}`,
  borderRadius: 6,
  width: 280,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === "light" ? "#24292e" : "#c9d1d9",
  backgroundColor: theme.palette.mode === "light" ? "#fff" : "#1c2128",
}));

const operation = (list1: any, list2: any, isUnion = false) =>
  list1.filter(
    (
      (set) => (a: any) =>
        isUnion === set.has(a.id)
    )(new Set(list2.map((b: any) => b.id))),
  );

// Following functions are to be used:
const inBoth = (list1: any, list2: any) => {
  if (!list1 || !list2 || list1?.length === 0 || list2?.length === 0) return [];
  return operation(list1, list2, true);
};

const NewStationSelector = () => {
  const selected_zones = useSelector(selectedZonesSelector);
  const selected_divisions = useSelector(selectedDivisionsSelector);
  const selected_customers = useSelector(selectedCustomersSelector)?.map((station) => ({
    id: station.customerId,
    value: station.customerName,
  }));

  const initial_multi_state = useMemo(
    () => ({
      zone_codes: selected_zones,
      div_codes: selected_divisions,
      customer_ids: selected_customers,
    }),
    [selected_zones, selected_divisions, selected_customers],
  );

  const [multiSelectFilterTempState, setMultiSelectFilterTempState] = useImmer<any>(initial_multi_state);
  const stateSyncedToRedux = useRef(false);
  const selected_zone = useSelector(singleSelectedZoneSelector);
  const selected_division = useSelector(singleSelectedDivisionSelector);
  const selected_station = useSelector(singleSelectedCustomerSelector);

  const initial_single_state = useMemo(
    () => ({
      zone_code: selected_zone,
      div_code: selected_division,
      customer_id: { id: selected_station.customerId, value: selected_station.customerName },
    }),
    [selected_zone, selected_division, selected_station],
  );
  const [singleSelectFilterTempState, setSingleSelectFilterTempState] = useImmer<any>(initial_single_state);

  const { updateCustomerIdList, updateCustomerId }: any = useContext(ActiveAlarmsData);

  const dispatch = useDispatch();
  let permissions = JSON.parse(localStorage.getItem("permissions") || "{}");
  const {
    outermostKeys: zone,
    outerToInnerKeys: division,
    innerToCustomerNames: station,
    customerNameToId: customer_name_to_id,
  }: any = extractUserDataFromPermission(permissions);

  const location = useLocation();
  const isSingleSelect = location.pathname.includes("yard-view");

  const selectedZone = isSingleSelect ? singleSelectFilterTempState.zone_code : multiSelectFilterTempState.zone_codes;
  const selectedDiv = isSingleSelect ? singleSelectFilterTempState.div_code : multiSelectFilterTempState.div_codes;
  const selectedStation = isSingleSelect
    ? singleSelectFilterTempState.customer_id
    : multiSelectFilterTempState.customer_ids;

  const popperRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const autocompleteSelectOpenRef = useRef(false);
  const autocompleteSelectOpenTimeoutRef = useRef<any>();


  const handleClickOpenDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e: any) => {
    if (popperRef.current?.contains(e.target)) return;
    if (autocompleteSelectOpenRef.current) return;
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const zoneDropdowns = useMemo(() => {
    if (!zone || zone.length === 0) return [];
    const zone_options = zone.map((zone_code: any) => {
      return { id: zone_code, value: zone_code };
    });

    if (!zone_options || zone_options.length === 0) return [];
    zone_options.sort((a: any, b: any) => a.value.localeCompare(b.value));

    if (multiSelectFilterTempState?.zone_codes?.length === zone?.length) {
      return [{ id: "deselect-all", value: "Deselect All" }, ...zone_options];
    }
    // if (!isSingleSelect) {
    //   if (selected_zones && selected_zones.length !== 0) {
    //     setMultiSelectFilterTempState((state: any) => {
    //       state.zone_codes = selected_zones;
    //     })
    //   }
    // }
    return [{ id: "select-all", value: "Select All" }, ...zone_options];
  }, [zone]);

  useEffect(() => {
    if (stateSyncedToRedux.current) return;
    if (selected_zones && selected_zones?.length !== 0) {
      setMultiSelectFilterTempState((state: any) => {
        state.zone_codes = selected_zones;
        state.div_codes = getAllDivisionsForZones(selected_zones);
        state.customer_ids = getAllStationForDivisions(getAllDivisionsForZones(selected_zones));
      });
      stateSyncedToRedux.current = true;
    }
  }, [selected_zones]);

  function getAllDivisionsForZones(zone_codes: any[]) {
    let div_list: any = [];
    (zone_codes ?? []).forEach((zoneCode: any) => {
      (division?.[zoneCode?.id] ?? []).forEach((divCode: any) => {
        div_list.push({ id: divCode, value: divCode });
      });
    });
    return div_list;
  }

  function getAllStationForDivisions(div_codes: any[]) {
    let stn_list: any = [];
    (div_codes ?? []).forEach((divCode: any) => {
      (station?.[divCode?.id] ?? []).forEach((stncode: any) => {
        stn_list.push({ id: customer_name_to_id?.[stncode]?.[0], value: stncode });
      });
    });
    return stn_list;
  }

  const divisionDropdown = useMemo(() => {
    if (isSingleSelect) {
      let div_list = division?.[singleSelectFilterTempState?.zone_code?.value ?? ""] ?? [];

      return div_list.map((divCode: any) => ({ id: divCode, value: divCode }));
    } else {
      const div_list = getAllDivisionsForZones(multiSelectFilterTempState?.zone_codes);
      if (!division || division.length === 0 || !div_list || div_list?.length === 0) return [];
      div_list.sort((a: any, b: any) => a.value.localeCompare(b.value));

      if (div_list?.length === 1) return div_list;

      if (multiSelectFilterTempState?.div_codes?.length === div_list?.length) {
        return [{ id: "deselect-all", value: "Deselect All" }, ...div_list];
      }
      // const div_codes = inBoth(div_list, multiSelectFilterTempState?.div_codes);
      // setMultiSelectFilterTempState((state: any) => ({ ...state, div_codes }));
      // return div_list;
      return [{ id: "select-all", value: "Select All" }, ...div_list];
    }
  }, [
    multiSelectFilterTempState?.zone_codes,
    multiSelectFilterTempState?.div_codes,
    singleSelectFilterTempState?.zone_code,
    isSingleSelect,
  ]);

  useEffect(() => {
    // when zone changes, then remove the division which were in the removed zone.
    const div_list = getAllDivisionsForZones(multiSelectFilterTempState?.zone_codes);
    const div_codes = inBoth(div_list, multiSelectFilterTempState?.div_codes);
    setMultiSelectFilterTempState((state: any) => ({ ...state, div_codes }));
  }, [multiSelectFilterTempState?.zone_codes, isSingleSelect]);

  const stationDropdown = useMemo(() => {
    if (isSingleSelect) {
      let stn_list = station?.[singleSelectFilterTempState?.div_code?.value ?? ""] ?? [];
      return stn_list.map((stnCode: any) => ({ id: customer_name_to_id?.[stnCode]?.[0], value: stnCode }));
    } else {
      const stn_list = getAllStationForDivisions(multiSelectFilterTempState?.div_codes);

      if (!station || station.length === 0 || !stn_list || stn_list?.length === 0) return [];

      stn_list.sort((a: any, b: any) => a.value.localeCompare(b.value));
      if (stn_list?.length === 1) return stn_list;

      if (multiSelectFilterTempState?.customer_ids?.length === stn_list?.length) {
        return [{ id: "deselect-all", value: "Deselect All" }, ...stn_list];
      }
      // const customer_ids = inBoth(stn_list, multiSelectFilterTempState?.customer_ids);
      // setMultiSelectFilterTempState((state: any) => ({ ...state, customer_ids }));
      // return stn_list;
      return [{ id: "select-all", value: "Select All" }, ...stn_list];
    }
  }, [
    multiSelectFilterTempState?.div_codes,
    multiSelectFilterTempState?.customer_ids,
    singleSelectFilterTempState?.div_code,
    isSingleSelect,
  ]);

  useEffect(() => {
    // when division changes, then remove the station which were in the removed division.
    const stn_list = getAllStationForDivisions(multiSelectFilterTempState?.div_codes);
    const customer_ids = inBoth(stn_list, multiSelectFilterTempState?.customer_ids);
    setMultiSelectFilterTempState((state: any) => ({ ...state, customer_ids }));
  }, [multiSelectFilterTempState?.div_codes, isSingleSelect]);

  const open = Boolean(anchorEl);
  const id = open ? "station-popover--button" : undefined;

  type SingleDropdownType = { id: string; value: string };

  const handleApply = () => {
    if (isSingleSelect) {
      let error = "";
      if (!singleSelectFilterTempState.customer_id) {
        error = "Station is not selected";
      }
      if (!singleSelectFilterTempState.div_code) {
        error = "Division is not selected";
      }
      if (!singleSelectFilterTempState.zone_code) {
        error = "Zone is not selected";
      }

      if (error) {
        toast.error(error);
        return;
      }

      toast.success("Applied Successfully");

      localStorage.setItem("customerId", singleSelectFilterTempState?.customer_id?.id ?? "");
      updateCustomerId?.();
      dispatch(setSelectedZone(singleSelectFilterTempState?.zone_code));
      dispatch(setSelectedDivision(singleSelectFilterTempState?.div_code));
      dispatch(
        setSelectedCustomer({
          customerId: singleSelectFilterTempState.customer_id.id,
          customerName: singleSelectFilterTempState.customer_id.value,
        }),
      );
    } else {
      function handleSetCustomers(
        zone_list: SingleDropdownType[],
        division_list: SingleDropdownType[],
        customers_list: SingleDropdownType[],
      ) {
        dispatch(setSelectedZones(zone_list));
        dispatch(setSelectedDivisions(division_list));
        dispatch(
          setSelectedCustomers(
            customers_list.map((station) => ({ customerId: station.id, customerName: station.value })),
          ),
        );
        localStorage.setItem("STN", customers_list.map((station) => station.value).toString());
        localStorage.setItem("customerIdList", JSON.stringify(customers_list.map((station) => station.id)));
        updateCustomerIdList?.();
      }
      if (!multiSelectFilterTempState.zone_codes || multiSelectFilterTempState?.zone_codes.length === 0) {
        toast.error("Please select at least one zone");
        return;
      }

      toast.success("Applied Successfully");

      if (!multiSelectFilterTempState.div_codes || multiSelectFilterTempState?.div_codes.length === 0) {
        const all_divisions = getAllDivisionsForZones(multiSelectFilterTempState.zone_codes);
        const all_stations = getAllStationForDivisions(all_divisions);
        setMultiSelectFilterTempState((state: any) => {
          state.div_codes = all_divisions;
          state.customer_ids = all_stations;
        });
        handleSetCustomers(multiSelectFilterTempState.zone_codes, all_divisions, all_stations);
        return;
      }

      if (!multiSelectFilterTempState.customer_ids || multiSelectFilterTempState?.customer_ids.length === 0) {
        const all_stations = getAllStationForDivisions(multiSelectFilterTempState.div_codes);
        setMultiSelectFilterTempState((state: any) => {
          state.customer_ids = all_stations;
        });
        handleSetCustomers(multiSelectFilterTempState.zone_codes, multiSelectFilterTempState.div_codes, all_stations);
        return;
      }
      handleSetCustomers(
        multiSelectFilterTempState.zone_codes,
        multiSelectFilterTempState.div_codes,
        multiSelectFilterTempState?.customer_ids,
      );
    }
  };

  function handleDiscard() {
    if (isSingleSelect) {
      setSingleSelectFilterTempState(initial_single_state);
    } else {
      setMultiSelectFilterTempState(initial_multi_state);
    }
  }

  // when single select, then make autocomplete to be able to select only one option, and also
  // when click on apply button, then check if zone, division, station all 3 should be selected, and then

  return (
    <>
      <Box>
        <button className="button--outlined" aria-describedby={id} onClick={handleClickOpenDropdown}>
          <span>Stations</span>
        </button>
      </Box>
      <StyledPopper id={id} ref={popperRef} open={open} anchorEl={anchorEl} placement="bottom-end">
        <ClickAwayListener onClickAway={handleClose}>
          <div className="flex flex-col gap-2">
            <Autocomplete
              size="small"
              multiple={!isSingleSelect}
              onOpen={() => {
                if(autocompleteSelectOpenTimeoutRef.current) {
                  clearTimeout(autocompleteSelectOpenTimeoutRef.current)
                }
                autocompleteSelectOpenRef.current = true
              }}
              onClose={() => autocompleteSelectOpenTimeoutRef.current =  setTimeout(() => (autocompleteSelectOpenRef.current = false), 400)}
              disableCloseOnSelect={!isSingleSelect}
              options={zoneDropdowns}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField className="filter-input" {...params} size="small" label="Zone" placeholder="Zone" />
              )}
              getOptionLabel={(zone) => zone.value}
              value={selectedZone}
              onChange={(_, newValue) => {
                if (isSingleSelect) {
                  setSingleSelectFilterTempState((state: any) => ({
                    ...state,
                    zone_code: newValue,
                    div_code: null,
                    customer_id: null,
                  }));
                } else {
                  for (const selected_value of newValue) {
                    if (selected_value.id === "select-all") {
                      setMultiSelectFilterTempState((state: any) => ({
                        ...state,
                        zone_codes: zone.map((zone_code: any) => {
                          return { id: zone_code, value: zone_code };
                        }),
                      }));
                      return;
                    }
                    if (selected_value.id === "deselect-all") {
                      setMultiSelectFilterTempState((state: any) => ({
                        ...state,
                        zone_codes: [],
                      }));
                      return;
                    }
                  }

                  setMultiSelectFilterTempState((state: any) => ({
                    ...state,
                    zone_codes: newValue,
                  }));
                }
              }}
              renderTags={(value, getTagProps) => {
                return (
                  <div>
                    <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                    {value.length > 1 && (
                      <AutocompleteStationListPop title={value.map((zone) => zone?.value ?? "").toString()}>
                        <Chip size="small" label={`+${value.length - 1}`} />
                      </AutocompleteStationListPop>
                    )}
                  </div>
                );
              }}
            />
            <Autocomplete
              size="small"
              multiple={!isSingleSelect}
              onOpen={() => {
                if(autocompleteSelectOpenTimeoutRef.current) {
                  clearTimeout(autocompleteSelectOpenTimeoutRef.current)
                }
                autocompleteSelectOpenRef.current = true
              }}
              onClose={() => autocompleteSelectOpenTimeoutRef.current =  setTimeout(() => (autocompleteSelectOpenRef.current = false), 400)}
              disableCloseOnSelect={!isSingleSelect}
              options={divisionDropdown}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  className="filter-input"
                  {...params}
                  size="small"
                  label="Divisions"
                  placeholder="Divisions"
                />
              )}
              getOptionLabel={(division) => division.value}
              value={selectedDiv}
              onChange={(_, newValue) => {
                if (isSingleSelect) {
                  setSingleSelectFilterTempState((state: any) => ({
                    ...state,
                    div_code: newValue,
                    customer_id: null,
                  }));
                } else {
                  for (const selected_value of newValue) {
                    if (selected_value.id === "select-all") {
                      setMultiSelectFilterTempState((state: any) => ({
                        ...state,
                        div_codes: getAllDivisionsForZones(multiSelectFilterTempState?.zone_codes),
                      }));
                      return;
                    }
                    if (selected_value.id === "deselect-all") {
                      setMultiSelectFilterTempState((state: any) => ({
                        ...state,
                        div_codes: [],
                      }));
                      return;
                    }
                  }

                  setMultiSelectFilterTempState((state: any) => ({
                    ...state,
                    div_codes: newValue,
                  }));
                }
              }}
              renderTags={(value, getTagProps) => {
                return (
                  <div>
                    <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                    {value.length > 1 && (
                      <AutocompleteStationListPop title={value.map((division) => division?.value ?? "").toString()}>
                        <Chip size="small" label={`+${value.length - 1}`} />
                      </AutocompleteStationListPop>
                    )}
                  </div>
                );
              }}
            />
            <Autocomplete
              size="small"
              multiple={!isSingleSelect}
              onOpen={() => {
                if(autocompleteSelectOpenTimeoutRef.current) {
                  clearTimeout(autocompleteSelectOpenTimeoutRef.current)
                }
                autocompleteSelectOpenRef.current = true
              }}
              onClose={() => autocompleteSelectOpenTimeoutRef.current =  setTimeout(() => (autocompleteSelectOpenRef.current = false), 400)}
              disableCloseOnSelect={!isSingleSelect}
              options={stationDropdown}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField className="filter-input" {...params} size="small" label="Station" placeholder="Station" />
              )}
              getOptionLabel={(customer) => customer.value}
              value={selectedStation}
              onChange={(_, newValue) => {
                if (isSingleSelect) {
                  setSingleSelectFilterTempState((state: any) => ({
                    ...state,
                    customer_id: newValue,
                  }));
                } else {
                  for (const selected_value of newValue) {
                    if (selected_value.id === "select-all") {
                      setMultiSelectFilterTempState((state: any) => ({
                        ...state,
                        customer_ids: getAllStationForDivisions(multiSelectFilterTempState?.div_codes),
                      }));
                      return;
                    }
                    if (selected_value.id === "deselect-all") {
                      setMultiSelectFilterTempState((state: any) => ({
                        ...state,
                        customer_ids: [],
                      }));
                      return;
                    }
                  }

                  setMultiSelectFilterTempState((state: any) => ({
                    ...state,
                    customer_ids: newValue,
                  }));
                }
              }}
              renderTags={(value, getTagProps) => {
                return (
                  <div>
                    <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                    {value.length > 1 && (
                      <AutocompleteStationListPop title={value.map((stn) => stn?.value ?? "").toString()}>
                        <Chip size="small" label={`+${value.length - 1}`} />
                      </AutocompleteStationListPop>
                    )}
                  </div>
                );
              }}
            />
            <div style={{ padding: "0.5rem" }} className="flex items-center gap-2">
              {/* <button onClick={handleDiscard} className="button--secondary w-full flex-1">
                <span>Discard</span>
              </button> */}
              <button onClick={handleApply} className="button--secondary w-full flex-1">
                <span>Apply</span>
              </button>
            </div>
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  );
};

export default NewStationSelector;